import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/cn";

export type SliderSize = "xs" | "sm" | "md" | "lg";

export type SliderProps = Omit<InputHTMLAttributes<HTMLInputElement>, "defaultValue" | "max" | "min" | "onChange" | "size" | "step" | "type" | "value"> & {
  value: number;
  onValueChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  label?: ReactNode;
  helperText?: ReactNode;
  errorText?: ReactNode;
  size?: SliderSize;
  formatValue?: (value: number) => ReactNode;
  containerClassName?: string;
  labelClassName?: string;
  valueClassName?: string;
  inputClassName?: string;
};

const sliderSizeClassName: Record<SliderSize, string> = {
  xs: "bfrs-h-5",
  sm: "bfrs-h-6",
  md: "bfrs-h-7",
  lg: "bfrs-h-8"
};

const valueTextSizeClassName: Record<SliderSize, string> = {
  xs: "bfrs-text-[11px]",
  sm: "bfrs-text-xs",
  md: "bfrs-text-xs",
  lg: "bfrs-text-sm"
};

const getAriaValueText = (value: ReactNode) => {
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  return undefined;
};

export const Slider = forwardRef<HTMLInputElement, SliderProps>(
  (
    {
      className,
      containerClassName,
      labelClassName,
      valueClassName,
      inputClassName,
      id,
      value,
      onValueChange,
      min = 0,
      max = 100,
      step = 1,
      disabled,
      label,
      helperText,
      errorText,
      size = "md",
      formatValue,
      "aria-describedby": ariaDescribedBy,
      "aria-invalid": ariaInvalid,
      "aria-valuetext": ariaValueText,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const helperId = helperText ? `${inputId}-helper` : undefined;
    const errorId = errorText ? `${inputId}-error` : undefined;
    const describedBy = [ariaDescribedBy, helperId, errorId].filter(Boolean).join(" ") || undefined;
    const displayValue = formatValue ? formatValue(value) : value;

    return (
      <div className={cn("bfrs-w-full", containerClassName, className)}>
        {(label || displayValue !== undefined) && (
          <div className="bfrs-mb-2 bfrs-flex bfrs-items-center bfrs-justify-between bfrs-gap-3">
            {label ? (
              <label htmlFor={inputId} className={cn("bfrs-text-[12px] bfrs-font-semibold bfrs-text-text-primary", disabled && "bfrs-text-text-disabled", labelClassName)}>
                {label}
              </label>
            ) : (
              <span />
            )}
            <output htmlFor={inputId} className={cn("bfrs-shrink-0 bfrs-font-semibold bfrs-text-text-primary", disabled && "bfrs-text-text-disabled", valueTextSizeClassName[size], valueClassName)}>
              {displayValue}
            </output>
          </div>
        )}
        <input
          ref={ref}
          id={inputId}
          type="range"
          value={value}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          aria-describedby={describedBy}
          aria-invalid={ariaInvalid ?? Boolean(errorText)}
          aria-valuetext={ariaValueText ?? getAriaValueText(displayValue)}
          className={cn(
            "bfrs-w-full bfrs-cursor-pointer bfrs-accent-primary bfrs-outline-none bfrs-transition focus-visible:bfrs-ring-2 focus-visible:bfrs-ring-border-focus disabled:bfrs-cursor-not-allowed disabled:bfrs-opacity-50",
            sliderSizeClassName[size],
            inputClassName
          )}
          onChange={(event) => onValueChange(event.currentTarget.valueAsNumber)}
          {...props}
        />
        <div className="bfrs-mt-1 bfrs-flex bfrs-justify-between bfrs-text-[11px] bfrs-font-medium bfrs-text-text-disabled">
          <span>{min}</span>
          <span>{max}</span>
        </div>
        {errorText ? (
          <p id={errorId} className="bfrs-mt-1.5 bfrs-text-xs bfrs-leading-5 bfrs-text-danger">
            {errorText}
          </p>
        ) : helperText ? (
          <p id={helperId} className="bfrs-mt-1.5 bfrs-text-xs bfrs-leading-5 bfrs-text-text-muted">
            {helperText}
          </p>
        ) : null}
      </div>
    );
  }
);

Slider.displayName = "Slider";
